import { Box } from '@material-ui/core';
import React, { FC, useContext } from 'react';
import { useSelector } from 'react-redux';
import { ShippingLabelProvider } from '@prisma/client';
import {
  Button,
  ConditionalWrapper,
  ExternalLink,
  IconCashFilled,
  IconCheckCircleFilled,
  IconShippingLabelFilled,
  InlineTextButton,
  NamedLink,
} from '..';
import TypographyWrapper, { TypographyFormat } from '../TypographyWrapper/TypographyWrapper';
import { getOwnListingsById } from '../../containers/ManageSalesPage/ManageSalesPage.duck';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import {
  BundleStatus,
  BundleType,
  ItemStatus,
  Participant,
  useGetBundleShipFromAddress,
} from '../../types/apollo/generated/types.generated';
import { BundleInfo } from '../../types/models/bundle';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import { OwnListing } from '../../types/sharetribe/listing';
import { FulfillmentMethod } from '../../types/shopConfig/shopConfigV2';
import css from './BundleActions.module.css';
import { types as sdkTypes } from '../../util/sdkLoader';
import AppContext from '../../context/AppContext';
import { useSellerGeneratedLabels } from '../../hooks/useSellerGeneratedLabels';
import { Loading } from '../../containers/ListingFlowV2/Loading';

const { UUID } = sdkTypes;

interface SellerActionsProps {
  bundle: BundleInfo;
  onGetPaidClick?: (bundle: BundleInfo) => void;
  onMarkAsFulfilledClick?: (bundle: BundleInfo) => void;
}

const SellerActions: FC<SellerActionsProps> = (props) => {
  const { bundle, onGetPaidClick, onMarkAsFulfilledClick } = props;
  const { fulfillment, id, isMarkedFulfilled, bundleItems } = bundle;

  const isSellerGeneratedLabelsEnabled = useSellerGeneratedLabels();

  const currentUser = useSelector<any>((state) => state.user.currentUser) as
    | CurrentUser
    | undefined;
  const { isBrand } = useCurrentUserPermissions();

  const firstListingId = bundleItems?.[0]?.listing.sharetribeListingId;
  const ownListings = useSelector<any>((state) =>
    getOwnListingsById(state, [new UUID(firstListingId)])
  ) as OwnListing[];

  // If the listing author has a defined fulfillmentMethod value, use that to control
  // the fulfillment settings. Otherwise, we will use the computed hasFulfillmentOffTreet flag
  // for brand direct items by default.
  const userFulfillmentMethod = currentUser?.attributes?.profile?.publicData?.fulfillmentMethod;
  const { defaultBrandFulfillmentMethod, css: shopCss } = useShopConfigV2();

  let fulfillmentMethod: FulfillmentMethod;
  if (isSellerGeneratedLabelsEnabled) {
    fulfillmentMethod = FulfillmentMethod.Peer;
  } else if (userFulfillmentMethod) {
    fulfillmentMethod = userFulfillmentMethod;
  } else if (isBrand) {
    fulfillmentMethod = defaultBrandFulfillmentMethod as FulfillmentMethod;
  } else {
    fulfillmentMethod = FulfillmentMethod.Treet;
  }

  const { data: peerFulfilledBundleAddressData, loading: peerFulfilledBundleAddressLoading } =
    useGetBundleShipFromAddress({
      variables: {
        bundleId: bundle.id,
      },
      skip: fulfillmentMethod !== FulfillmentMethod.Peer || !fulfillment,
    });

  // Ignore actions if a brand isn't fulfilling through Treet's dash
  if (fulfillmentMethod === FulfillmentMethod.Shopify) return null;

  const handlePrintShippingLabelClick = () => {
    const labelUrl = fulfillment?.labelURL || '';
    window.open(labelUrl, '_blank');
  };

  const hasShippingLabel = !!fulfillment?.labelURL;
  const canRecreateLabel =
    !!fulfillment && fulfillment.shippingLabelProvider !== ShippingLabelProvider.SUPER_CIRCLE;

  const handleClickWithPreventDefault =
    (onClickHandler: (bundle: BundleInfo) => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onClickHandler(bundle);
    };

  if (bundle.status === BundleStatus.Open && !isMarkedFulfilled) {
    if (fulfillmentMethod === FulfillmentMethod.Peer) {
      const enterTrackingDetailsPageButton = (
        <NamedLink name="EnterTrackingDetailsPage" params={{ bundleId: bundle.id }}>
          <Button className={css.actionButton}>Ship and add tracking details</Button>
        </NamedLink>
      );

      if (!fulfillment) {
        return enterTrackingDetailsPageButton;
      }

      if (peerFulfilledBundleAddressLoading) {
        return <Loading />;
      }

      if (!peerFulfilledBundleAddressData?.bundle?.shipFromAddress) {
        return enterTrackingDetailsPageButton;
      }

      return null;
    }
    return (
      <>
        {(fulfillment || isBrand) && onMarkAsFulfilledClick && (
          <Button
            className={css.actionButton}
            onClick={handleClickWithPreventDefault(onMarkAsFulfilledClick)}
          >
            <IconCheckCircleFilled
              className={css.actionIcon}
              color={shopCss?.primaryButton?.font?.color}
            />
            {isBrand ? 'Mark as Fulfilled' : 'Mark as Shipped'}
          </Button>
        )}
        <ConditionalWrapper
          condition={!hasShippingLabel}
          wrapper={(children) => (
            <NamedLink
              name="GenerateShippingLabelPage"
              params={{ id }}
              style={{ textDecoration: 'none' }}
            >
              {children}
            </NamedLink>
          )}
        >
          <Button
            className={css.actionButton}
            onClick={!hasShippingLabel ? undefined : handlePrintShippingLabelClick}
          >
            <IconShippingLabelFilled
              className={css.actionIcon}
              color={shopCss?.primaryButton?.font?.color}
            />
            Print Shipping Label
          </Button>
        </ConditionalWrapper>
        {hasShippingLabel && canRecreateLabel && (
          <Box display="flex" justifyContent="center" pt={3} pb={1}>
            <NamedLink name="GenerateShippingLabelPage" params={{ id }}>
              <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
                Recreate Shipping Label
              </TypographyWrapper>
            </NamedLink>
          </Box>
        )}
      </>
    );
  }

  const hasVerifiedBundleItems = bundle.bundleItems.some(
    (bundleItem) => bundleItem.status === ItemStatus.Verified
  );
  const payoutOption = ownListings?.[0]?.attributes?.privateData?.payoutOption;

  if (
    bundle.status === BundleStatus.Resolved &&
    hasVerifiedBundleItems &&
    !payoutOption &&
    onGetPaidClick
  ) {
    return (
      <Button className={css.actionButton} onClick={handleClickWithPreventDefault(onGetPaidClick)}>
        <IconCashFilled className={css.actionIcon} color="inherit" />
        Get Paid!
      </Button>
    );
  }

  return null;
};

interface BuyerActionsProps {
  bundle: BundleInfo;
  onDisputeClick?: (bundle: BundleInfo) => void;
  onVerifyClick?: (bundle: BundleInfo) => void;
  onMarkAsDeliveredClick?: (bundle: BundleInfo) => void;
}

const BuyerActions: FC<BuyerActionsProps> = (props) => {
  const { bundle, onDisputeClick, onVerifyClick, onMarkAsDeliveredClick } = props;
  const { tryOnReturnLink } = useShopConfig();
  const { isTradeInAdmin } = useCurrentUserPermissions();
  const { canonicalRootUrl: shopUrl } = useContext(AppContext);

  const isTradeInBundle = bundle?.type === BundleType.TradeIn;
  const isDelivered = bundle.status === BundleStatus.Delivered;
  const isOpenOrShipped = [BundleStatus.Open, BundleStatus.Shipped].includes(bundle.status);
  const isTradeInAdminAndBundle = isTradeInAdmin && isTradeInBundle;
  const { isBrandDirect, isReturnable } = bundle;
  const shouldDisplayTryOnReturnLink = isBrandDirect && tryOnReturnLink;

  return (
    <Box pt="4px">
      {isOpenOrShipped && isTradeInAdmin && isTradeInBundle && onMarkAsDeliveredClick && (
        <Button className={css.actionButton} onClick={() => onMarkAsDeliveredClick(bundle)}>
          <IconCheckCircleFilled className={css.actionIcon} color="inherit" />
          Mark as Delivered
        </Button>
      )}
      {isDelivered && !isTradeInBundle && isReturnable && onDisputeClick && (
        <>
          <Button className={css.actionButton} onClick={() => onDisputeClick(bundle)}>
            Start A Return
          </Button>
          <Box textAlign="center" mt={2}>
            <TypographyWrapper variant="body2">
              {`Your order is covered by the `}
              <ExternalLink href={`${shopUrl}/about/buyer?no_scroll=true#return-refund-policy`}>
                <TypographyWrapper variant="body2" format={TypographyFormat.Underlined}>
                  Brand Direct return policy
                </TypographyWrapper>
              </ExternalLink>
            </TypographyWrapper>
          </Box>
        </>
      )}
      {isDelivered && !isTradeInBundle && !isReturnable && onVerifyClick && (
        <Button className={css.actionButton} onClick={() => onVerifyClick(bundle)}>
          <IconCheckCircleFilled className={css.actionIcon} color="inherit" />
          Verify Purchase
        </Button>
      )}

      {isDelivered && onDisputeClick && isTradeInAdminAndBundle && (
        <Button className={css.actionButton} onClick={() => onDisputeClick(bundle)}>
          Issue Credit
        </Button>
      )}

      {isDelivered && onDisputeClick && !isTradeInAdminAndBundle && !isReturnable && (
        <Box display="flex" justifyContent="center" mt={2} mb={1}>
          {!shouldDisplayTryOnReturnLink && (
            <InlineTextButton onClick={() => onDisputeClick(bundle)}>
              <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
                File A Misrepresentation Claim
              </TypographyWrapper>
            </InlineTextButton>
          )}
          {shouldDisplayTryOnReturnLink && (
            <ExternalLink href={tryOnReturnLink}>
              <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
                Start Your Brand Direct Return
              </TypographyWrapper>
            </ExternalLink>
          )}
        </Box>
      )}
    </Box>
  );
};

interface BundleActionsProps {
  bundle: BundleInfo;
  participant: Participant;
  onVerifyClick?: (bundle: BundleInfo) => void;
  onDisputeClick?: (bundle: BundleInfo) => void;
  onGetPaidClick?: (bundle: BundleInfo) => void;
  onMarkAsFulfilledClick?: (bundle: BundleInfo) => void;
  onMarkAsDeliveredClick?: (bundle: BundleInfo) => void;
}

// TODO(SY | TREET-718): Refactor this to be used on the ManageSalesMobile page
const BundleActions: FC<BundleActionsProps> = (props) => {
  const {
    bundle,
    participant,
    onVerifyClick,
    onDisputeClick,
    onGetPaidClick,
    onMarkAsFulfilledClick,
    onMarkAsDeliveredClick,
  } = props;
  const isBuyer = participant === Participant.Buyer;
  const isSeller = participant === Participant.Seller;

  if (isSeller) {
    return (
      <SellerActions
        bundle={bundle}
        onGetPaidClick={onGetPaidClick}
        onMarkAsFulfilledClick={onMarkAsFulfilledClick}
      />
    );
  }
  if (isBuyer) {
    return (
      <BuyerActions
        bundle={bundle}
        onDisputeClick={onDisputeClick}
        onVerifyClick={onVerifyClick}
        onMarkAsDeliveredClick={onMarkAsDeliveredClick}
      />
    );
  }

  return null;
};

export default BundleActions;
